import Cookies from 'universal-cookie';

let cookies = new Cookies();

export const createNewCookie = (key, value) => {
    // Historically this function cleared all cookies before setting a new session.
    // Keep behavior for session_token, but avoid using it for additional auth tokens.
    removeAllCookies();
    return setCookie(key, value);
};

export const setCookie = (key, value, {expires} = {}) => {
    const now = new Date();
    const expirationDate = expires || new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

    try {
        // Set the cookie
        cookies.set(key, value, {
            path: '/',
            expires: expirationDate,
            secure: window.location.protocol === 'https:',
            sameSite: 'lax'
        });

        // Immediately try to read the cookie
        const verifiedValue = cookies.get(key);

        // If immediate verification fails, try again after a short delay
        if (verifiedValue !== value) {
            setTimeout(() => {
                const delayedVerifiedValue = cookies.get(key);
                if (delayedVerifiedValue !== value) {
                    // Attempt to set the cookie again
                    cookies.set(key, value, {
                        path: '/',
                        expires: expirationDate,
                        secure: window.location.protocol === 'https:',
                        sameSite: 'lax'
                    });
                }
            }, 100);
        }
    } catch (error) {
        console.error('Error setting cookie:', error);
    }
};

export const getCookie = (name) => {
    return cookies.get(name);
};

// RFC 6265 compliant cookie name validation
// Cookie names can contain: !#$%&'*+-.0-9A-Z^_`a-z|~
const isValidCookieName = (name) => {
    if (!name || typeof name !== 'string') return false;
    const trimmed = name.trim();
    if (trimmed.length === 0) return false;
    // Check if name contains only valid cookie name characters
    // RFC 6265: cookie-name = token (where token is defined as: 1*<any CHAR except CTLs or separators>)
    // Simplified: allow alphanumeric, dash, underscore, and some special chars
    return /^[!#$%&'*+\-.0-9A-Z^_`a-z|~]+$/.test(trimmed);
};

export const removeCookie = (name) => {
    try {
        // Validate cookie name before attempting to remove
        if (isValidCookieName(name)) {
            cookies.remove(name, {path: '/'});
        } else {
            console.warn('Skipping invalid cookie name:', name, typeof name);
        }
    } catch (error) {
        console.error('Error removing cookie:', name, error);
    }
};

export const removeAllCookies = () => {
    try {
        const allCookies = cookies.getAll();
        console.log('removeAllCookies: Found cookies:', Object.keys(allCookies));
        
        Object.keys(allCookies).forEach((cookie) => {
            try {
                // Validate cookie name before attempting to remove
                if (isValidCookieName(cookie)) {
                    console.log('removeAllCookies: Removing cookie:', cookie);
                    cookies.remove(cookie, {path: '/'});
                } else {
                    console.warn('removeAllCookies: Skipping invalid cookie name:', cookie, 'type:', typeof cookie);
                }
            } catch (error) {
                // Catch and log errors for individual cookies, but continue processing others
                console.error('removeAllCookies: Error removing cookie:', cookie, error);
            }
        });
    } catch (error) {
        // Catch any errors from getAll() itself
        console.error('removeAllCookies: Error getting all cookies:', error);
    }
};

const dec2hex = (dec) => {
    return dec.toString(16).padStart(2, '0');
};

export const generateId = (len) => {
    const arr = new Uint8Array((len || 40) / 2);
    window.crypto.getRandomValues(arr);
    return Array.from(arr, dec2hex).join('');
};

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const getSessionTokenWithRetry = async (retries = 5, interval = 1000) => {
    for (let i = 0; i < retries; i++) {
        const token = getCookie('session_token');
        if (token) return token;
        // Wait for a bit before retrying
        await delay(interval);
    }
    return null;
};

let currentToken = null;

// Initializes the token
export const initializeToken = async () => {
    currentToken = await getSessionTokenWithRetry();
    return currentToken;
};

export const getCurrentToken = () => {
    return currentToken;
};

// New function to clear the current token
export const clearCurrentToken = () => {
    currentToken = null;
};

// New function to refresh the token
export const refreshToken = async () => {
    clearCurrentToken();
    return await initializeToken();
};

// Loyalty Cloud v2: short-lived bearer token used for loyalty-cloud backend requests
// (replaces legacy LCAPIKey)
const LC_SERVICE_TOKEN_COOKIE = 'lc_service_token';

const _safeBase64UrlDecode = (s) => {
    try {
        const base64 = s.replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
        return atob(padded);
    } catch {
        return null;
    }
};

export const decodeJwtPayload = (jwt) => {
    if (!jwt || typeof jwt !== 'string') return null;
    const parts = jwt.split('.');
    if (parts.length < 2) return null;
    const json = _safeBase64UrlDecode(parts[1]);
    if (!json) return null;
    try {
        return JSON.parse(json);
    } catch {
        return null;
    }
};

export const isJwtExpired = (jwt, {skewSeconds = 30} = {}) => {
    const payload = decodeJwtPayload(jwt);
    const exp = payload?.exp;
    if (!exp) return true;
    const nowSec = Math.floor(Date.now() / 1000);
    return nowSec >= Number(exp) - skewSeconds;
};

export const setLcServiceTokenCookie = (token) => {
    // Keep this short-lived and refreshable. Set a conservative max cookie expiry.
    // If token is invalid/undecodable, fall back to a short (2h) cookie.
    const payload = decodeJwtPayload(token);
    const expSec = payload?.exp;
    const expires = expSec ? new Date(Number(expSec) * 1000) : new Date(Date.now() + 2 * 60 * 60 * 1000);
    return setCookie(LC_SERVICE_TOKEN_COOKIE, token, {expires});
};

export const getLcServiceTokenCookie = () => getCookie(LC_SERVICE_TOKEN_COOKIE);

export const clearLcServiceTokenCookie = () => removeCookie(LC_SERVICE_TOKEN_COOKIE);
